import { debounce } from 'lodash';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MAILCHIMP_FIELDS, MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY, RATE_LIMIT_ERROR_MESSAGE } from 'src/utils/const';
import { callApi } from 'src/core/callApi';
import { SubscriptionModalOrigin, SubscriptionPlan } from 'src/interfaces/idashboard';
import { User } from 'src/interfaces/iuser';
import { getTokenThunk } from 'src/store/Auth/actions';
import { setUserDataToMailChimp, validateEmail } from 'src/utils/functions';
import { useCanAccessExpertPlan } from './useFeatureAccess';
import { setTailoredRegionCreationRateLimited } from 'src/store/Dashboard/actions';

export const useEmailValidation = (debounceDelay: number = 200) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const emailInput = event.target.value.trim().toLowerCase();
      setEmail(emailInput);

      // cleanups/re-initializations
      setEmailError(false);
      setEmailErrorMessage('');
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      // Set new debounce timeout
      const newDebounceTimeout = setTimeout(() => {
        if (!validateEmail(emailInput)) {
          setEmailError(true);
          setEmailErrorMessage('Please enter a valid email address.');
        } else {
          setEmailError(false);
          setEmailErrorMessage('');
        }
      }, debounceDelay);

      setDebounceTimeout(newDebounceTimeout);
    },
    [debounceTimeout, debounceDelay]
  );

  // Cleanup the timeout when component unmounts or on new renders
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  return { email, emailError, emailErrorMessage, handleEmailChange };
};

export const useIsLoggedIn = () => {
  const dispatch = useDispatch();
  const isLoggedIn = Boolean(dispatch(getTokenThunk()));
  return isLoggedIn;
};

export const useSubscription = (isLoggedIn, setLoginModalOpen) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const { triggerPaymentInterest } = useSetUserPaymentInterest();

  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [requiredSubscriptionPlan, setRequiredSubscriptionPlan] = useState<SubscriptionPlan>(
    'expert'
  );
  const [subscriptionModalView, setSubscriptionModalView] = useState<'basic' | 'detailed'>(
    'detailed'
  );
  const [
    subscriptionModalOrigin,
    setSubscriptionModalOrigin,
  ] = useState<SubscriptionModalOrigin | null>(null);
  const [showUpgradeSubscriptionModal, setShowUpgradeSubscriptionModal] = useState<
    null | 'expert' | 'scale'
  >(null);
  const [showSearchCreditDeductWarningModal, setShowSearchCreditDeductWarningModal] = useState(
    false
  );
  const [showTrial, setShowTrial] = useState(false);

  useEffect(() => {
    // cleanup
    if (!subscriptionModalOpen) {
      setSubscriptionModalView('detailed');
    }
  }, [subscriptionModalOpen]);

  const handleOnSampleClick = (
    reqSubscriptionPlan: SubscriptionPlan = 'expert',
    origin: SubscriptionModalOrigin | null = null
  ) => {
    const userSubscriptionPlan = user.subscriptionPlan;

    if (!isLoggedIn) {
      setLoginModalOpen(true);
      return;
    }

    // Allow 'expert' users access to all 'pro' requirements
    // Allow 'scale' users access to all 'pro' and 'expert' requirements
    const hasAccess =
      (reqSubscriptionPlan === 'pro' && ['expert', 'scale'].includes(userSubscriptionPlan)) ||
      (reqSubscriptionPlan === 'expert' && userSubscriptionPlan === 'scale') ||
      reqSubscriptionPlan === userSubscriptionPlan;

    if (hasAccess) {
      setShowSearchCreditDeductWarningModal(true);
    } else {
      triggerPaymentInterest();
      setSubscriptionModalView('basic');
      setRequiredSubscriptionPlan(reqSubscriptionPlan);
      setSubscriptionModalOrigin(origin);
      if (userSubscriptionPlan == 'starter') {
        setSubscriptionModalOpen(true);
      } else {
        reqSubscriptionPlan !== 'pro'
          ? setShowUpgradeSubscriptionModal(reqSubscriptionPlan as 'expert' | 'scale')
          : setSubscriptionModalOpen(true);
      }
    }
  };

  const resetSubscriptionModal = () => {
    setRequiredSubscriptionPlan('expert');
    setSubscriptionModalView('detailed');
    setSubscriptionModalOrigin(null);
  };

  return {
    subscriptionModalOpen,
    setSubscriptionModalOpen,
    requiredSubscriptionPlan,
    setRequiredSubscriptionPlan,
    subscriptionModalView,
    setSubscriptionModalView,
    subscriptionModalOrigin,
    setSubscriptionModalOrigin,
    showUpgradeSubscriptionModal,
    setShowUpgradeSubscriptionModal,
    showSearchCreditDeductWarningModal,
    setShowSearchCreditDeductWarningModal,
    handleOnSampleClick,
    showTrial,
    setShowTrial,
    resetSubscriptionModal,
  };
};

export const useUpdateCustomMarketName = (tailorRegionName, setTailorRegionName) => {
  const [nameUpdateStatus, setNameUpdateStatus] = useState({ error: false, message: '' });
  const [nameLoading, setNameLoading] = useState(false);
  const [editableName, setEditableName] = useState('');
  const originalGeneratedName = useRef(tailorRegionName);
  const dispatch = useDispatch();

  useEffect(() => {
    originalGeneratedName.current = tailorRegionName;
  }, [tailorRegionName]);

  const checkOrUpdateTailoredRegionName = async (
    action: 'create' | 'check' | 'rename' = 'check',
    newtailoredRegionName: string = editableName
  ): Promise<any> => {
    action === 'check' && setNameLoading(true);

    dispatch(setTailoredRegionCreationRateLimited(false));

    if (
      (action === 'check' && newtailoredRegionName.match(/[^a-zA-Z0-9,-_ ]/g)) ||
      newtailoredRegionName.includes('/')
    ) {
      setNameUpdateStatus({
        error: true,
        message:
          'Invalid name: only alphanumeric characters, spaces, commas, hyphens, and underscores are allowed.',
      });
      setNameLoading(false);
      return;
    }

    const editTailorRegionName =
      'https://oz287au41h.execute-api.us-east-2.amazonaws.com/default/createTailorRegion';

    const editTailorRegionConfig = {
      queryParams: {
        generatedName: originalGeneratedName.current,
        customName: newtailoredRegionName?.trim(),
        action: action,
        apiType: 'v2'
      },
    };

    try {
      const editTailorRegionResponse = await callApi(
        editTailorRegionName,
        editTailorRegionConfig,
        true,
        false,
        '',
        'GET',
        true,
        undefined,
        true,
        true
      );

      if (editTailorRegionResponse && editTailorRegionResponse.status === 429) {
        dispatch(setTailoredRegionCreationRateLimited(true));
        console.log('Rate limit error');
        return;
      }

      const editTailorRegionResponseObj = editTailorRegionResponse.message;
      const isCustomNameAllowed = editTailorRegionResponseObj.isCustomNameAllowed;

      if (isCustomNameAllowed) {
        setNameUpdateStatus({
          error: false,
          message: 'Name is available!',
        });
        action !== 'check' && setTailorRegionName(newtailoredRegionName?.trim()); // Update name on success
      } else if (action == 'check' && !isCustomNameAllowed) {
        setNameUpdateStatus({
          error: true,
          message: 'This market name already exists. Please choose a different name.',
        });
      }
    } catch (e) {
      console.log('Error while calling /createTailorRegion:', e);
      setNameUpdateStatus({
        error: true,
        message: 'An error occurred. Please try again.',
      });
      dispatch(setTailoredRegionCreationRateLimited(false));
    }
    action === 'check' && setNameLoading(false);
  };

  const debouncedUpdateTailoredRegionName = useCallback(
    debounce(newName => checkOrUpdateTailoredRegionName('check', newName), 300),
    []
  );

  // Cleanup debounce on component unmount
  useEffect(() => {
    return () => {
      debouncedUpdateTailoredRegionName.cancel();
    };
  }, [debouncedUpdateTailoredRegionName]);

  const handleNameCheck = e => {
    const newName = e.target.value;
    setEditableName(newName);
    debouncedUpdateTailoredRegionName(newName);
  };

  return {
    editableName,
    setEditableName,
    nameLoading,
    nameUpdateStatus,
    handleNameCheck,
    checkOrUpdateTailoredRegionName,
  };
};

export const useSetUserPaymentInterest = () => {
  const STATUS = {
    IDLE: 'idle',
    LOADING: 'loading',
    ALREADY_SET: 'already_set',
    SET_SUCCESS: 'set_success',
    SET_ERROR: 'set_error',
  };

  const [status, setStatus] = useState<string>(STATUS.IDLE);
  const user = useSelector((state: any) => state.dashboard.user);

  const triggerPaymentInterest = () => {
    const alreadySet = localStorage.getItem('is-shown-payment-interest');

    // console.log("Triggering Payment Interest!");

    if (alreadySet || user?.email === 'Trial') {
      setStatus(STATUS.ALREADY_SET);
      return;
    }

    setStatus(STATUS.LOADING);

    setUserDataToMailChimp([[MAILCHIMP_FIELDS.HAS_SHOWN_PAYMENT_INTEREST, true]])
      .then(() => {
        localStorage.setItem('is-shown-payment-interest', 'true');
        setStatus(STATUS.SET_SUCCESS);
      })
      .catch(() => {
        setStatus(STATUS.SET_ERROR);
      });
  };

  return { status, triggerPaymentInterest };
};

export const UseOverviewIndividualLazyLoadingKPICacheAccessStatus = (
  cachedMarketData,
  isDemoMarket,
  currentKpiData
) => {
  const isMarketCacheLoading = cachedMarketData.loading;
  const isCurrentKpiDataCacheLoading = currentKpiData == 'processing';
  const isUserAllowedToAccessThisFeature = useCanAccessExpertPlan();
  const isCurrentFeatureLocked = !isDemoMarket && !isUserAllowedToAccessThisFeature;

  const isLoading = isMarketCacheLoading || isCurrentKpiDataCacheLoading;
  const hasRealData = currentKpiData;
  const isDataBlur = !isMarketCacheLoading && (isCurrentFeatureLocked || !hasRealData);
  const renderGraphComponent =
    (isCurrentFeatureLocked && !isMarketCacheLoading) ||
    (!isCurrentFeatureLocked && !isMarketCacheLoading && !isCurrentKpiDataCacheLoading);

  return {
    isLoading,
    renderGraphComponent,
    isDataBlur,
    isUserAllowedToAccessThisFeature,
    isCurrentKpiDataCacheLoading,
  };
};

export const useVersionedCallApi = () => {
  const callVersionRef = useRef(0);

  const versionedCallApi = useCallback(
    async (
      url: string,
      config: any,
      auth: boolean,
      gzip: boolean = false,
      key: string = '',
      method: string = 'GET',
      returnErrorJson: boolean = false,
      onResult: (data: any) => void,
      returnErrorStatusCode?: boolean,
      onRateLimitExceeded?: () => void
    ) => {
      const currentVersion = ++callVersionRef.current;

      try {
        const result = await callApi(url, config, auth, gzip, key, method, returnErrorJson, undefined, returnErrorStatusCode);
        
        if (currentVersion === callVersionRef.current) {
          if (returnErrorStatusCode && result.status === 429) {
            onRateLimitExceeded && onRateLimitExceeded();
          } else {
            onResult(result); // consider the latest response
          }
        } else {
          // ignore the previous response
        }
      } catch (error) {
        if (currentVersion === callVersionRef.current) {
          onResult({ error });
        }
      }
    },
    []
  );

  return { versionedCallApi };
}

export const useGetCreditsRemaining = (returnTotalCredits: boolean = false) => {
  const user: User = useSelector((state: any) => state.dashboard.user);
  const currentYearMonthString = new Date().toISOString().slice(0, 7);
  const creditsUsed = user.marketExplorerRowDataExportUsage[currentYearMonthString] || 0;
  const creditsRemaining = MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY - creditsUsed;

  if (returnTotalCredits) {
    return { creditsRemaining, creditsUsed, totalCredits: MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY };
  }

  return { creditsRemaining, creditsUsed };
}
